import { useMemo, useState } from "react";
import { EVENTS, PICKS, TICKER, STATUS_META } from "@/mocks/gossip";

const NAV = [
  { id: "hot", label: "实时热瓜榜" },
  { id: "reversal", label: "反转追踪" },
  { id: "editor", label: "编辑精选" },
  { id: "category", label: "分类瓜田" },
  { id: "faq", label: "热瓜问答" },
  { id: "about", label: "关于我们" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    const hit = (t: string) => t.toLowerCase().includes(q.toLowerCase());
    const ev = EVENTS.filter((e) => hit(e.title) || hit(e.category) || hit(e.summary)).map((e) => ({
      type: "事件",
      label: e.title,
      href: `#ev-${e.id}`,
      status: STATUS_META[e.status].label,
    }));
    const pk = PICKS.filter((p) => hit(p.title) || hit(p.category)).map((p) => ({
      type: "精选",
      label: p.title,
      href: "#editor",
      status: p.category,
    }));
    const tk = TICKER.filter((t) => hit(t)).map((t) => ({ type: "热讯", label: t, href: "#hot", status: "快讯" }));
    return [...ev, ...pk, ...tk].slice(0, 8);
  }, [query]);

  const goSearch = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setQuery("");
    setSearchOpen(false);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* 顶部实时跑马灯 */}
      <div className="bg-ink-900 text-paper-50">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 h-8 flex items-center gap-3 overflow-hidden">
          <span className="flex items-center gap-1.5 whitespace-nowrap text-xs font-bold tracking-widest text-amber-300">
            <span className="w-2 h-2 rounded-full bg-brand-500 pulse-dot" />
            今日热瓜
          </span>
          <div className="relative flex-1 overflow-hidden marquee-mask h-full">
            <div className="flex w-max ticker-track h-full items-center">
              {[...TICKER, ...TICKER].map((t, i) => (
                <span key={i} className="whitespace-nowrap text-xs text-paper-100/85 pr-12">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <span className="hidden sm:inline-block whitespace-nowrap text-[11px] text-paper-100/60">滚动刷新中</span>
        </div>
      </div>

      {/* 主导航 */}
      <div className="bg-paper-50/95 backdrop-blur border-b border-ink-200/70">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 flex items-center gap-4 h-16">
          <a href="#top" className="flex items-center gap-2 group" aria-label="今日热瓜 首页">
            <span className="w-9 h-9 rounded-lg bg-brand-500 flex items-center justify-center text-paper-50 shadow-soft">
              <i className="ri-fire-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <h1 className="font-heading font-black text-xl text-ink-900 group-hover:text-brand-600 transition-colors">
                今日热瓜
              </h1>
              <span className="text-[10px] tracking-[0.2em] text-ink-500 mt-0.5">追瓜到最新</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-1 ml-6" aria-label="主导航">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                className="px-3 py-2 text-sm text-ink-700 hover:text-brand-600 rounded-md hover:bg-brand-50 whitespace-nowrap transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setSearchOpen((s) => !s)}
              aria-label="搜索热瓜"
              className="w-9 h-9 rounded-full border border-ink-200 text-ink-700 hover:text-brand-600 hover:border-brand-400 hover:bg-brand-50 flex items-center justify-center transition-colors"
            >
              <i className={`${searchOpen ? "ri-close-line" : "ri-search-line"} text-lg`} />
            </button>
            <a
              href="#hot"
              className="hidden sm:inline-flex whitespace-nowrap items-center gap-1.5 px-4 h-9 rounded-full bg-brand-500 text-paper-50 text-sm font-semibold hover:bg-brand-600 transition-colors"
            >
              <i className="ri-flashlight-fill" />
              去追瓜
            </a>
            <button
              onClick={() => setMenuOpen((m) => !m)}
              aria-label="打开菜单"
              className="lg:hidden w-9 h-9 rounded-full border border-ink-200 flex items-center justify-center text-ink-700"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>

        {/* 站内搜索 */}
        {searchOpen && (
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 pb-4 animate-fadeUp">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-ink-400 text-lg" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜事件、节目、栏目名，例如：落日信箱 / 周棠 / 造型争议"
                className="w-full h-12 pl-12 pr-4 rounded-xl border border-ink-200 bg-paper-50 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </div>
            {query.trim() && (
              <div className="mt-2 rounded-xl border border-ink-200 bg-paper-50 overflow-hidden shadow-soft">
                {results.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-ink-500">没有找到相关热瓜，换个关键词试试。</p>
                ) : (
                  results.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => goSearch(r.href)}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-brand-50 transition-colors border-b border-ink-200/60 last:border-0"
                    >
                      <span className="w-12 shrink-0 rounded bg-ink-100 text-ink-600 text-center text-[11px] py-0.5">
                        {r.type}
                      </span>
                      <span className="flex-1 text-sm text-ink-800 line-clamp-1">{r.label}</span>
                      <span className="shrink-0 text-[11px] text-brand-600 whitespace-nowrap">{r.status}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* 移动端菜单 */}
        {menuOpen && (
          <nav
            className="lg:hidden border-t border-ink-200/70 bg-paper-50 px-4 py-3 grid grid-cols-2 gap-2"
            aria-label="移动端导航"
          >
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={() => setMenuOpen(false)}
                className="px-4 py-2.5 rounded-lg bg-paper-100 text-ink-800 text-sm font-medium flex items-center justify-between"
              >
                {n.label}
                <i className="ri-arrow-right-up-line text-ink-400" />
              </a>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}